import uniq from 'utils/uniq';
import uniqBy from 'utils/uniqBy';
import flatten from 'utils/flatten';
import ordinal from 'utils/ordinal';
import queryString from 'utils/queryString';
import httpRequest from 'utils/httpRequest';
import uuid from 'utils/uuid';
import { formatDate, parseDate } from 'utils/format';

import { apiClientGetRequest, apiClientPostRequest } from '../lib/backendRequest';
import SETTINGS from '../settings';

/**
 * Prepare URL with Api Gateway HOST and current query.
 * @param {string} url
 * @param {object} query
 * @returns {string}
 */
const withApiGatewayHost = (url, query = {}) => {
  return queryString.stringifyUrl(`${SETTINGS.API_GATEWAY_HOST}/${url}`, query);
};

/**
 * Generate Thumb URL. Usable for Media and Record.
 * @param {string} id
 * @param {string} fext
 * @param {number} namespace
 * @returns {string}
 */
export const ancestryThumbUrl = (id, fext = '', namespace = 1093) => {
  const extension = fext && fext.length > 0 ? `.${fext}` : '';
  return `https://mediasvc.ancestry.com/v2/thumbnail/namespaces/${namespace}/media/${id}${extension}?client=MyCanvas&MaxSide=500`;
};

/**
 * Generate Media Image URL. Usable only for Media!
 * @param {string} id
 * @param {string} fext
 * @param {number} namespace
 * @returns {string}
 */
export const ancestryMediaUrl = (id, fext = '', namespace = 1093) => {
  const extension = fext && fext.length > 0 ? `.${fext}` : '';
  return `https://mediasvc.ancestry.com/v2/image/namespaces/${namespace}/media/${id}${extension}?client=MyCanvas`;
};

/**
 * Transform the fetched tree media into list of photos.
 * Items that aren't photos are discarded.
 * The photos have undefined width and height, although API provides them.
 * But these provided sizes (`Size.Width` and `Size.Height`) are for some photos swapped.
 * This probably happens for images that are rotated through EXIF.
 * @param {Array<object>} data List of result returned from the API
 * @returns {Array<object>}
 */
export const transformTreeMedia = data =>
  data.reduce((arr, medium) => {
    if (['jpg', 'jpeg'].indexOf(medium.Fext) >= 0) {
      const personIds = [];
      // owner
      if (medium.PersonId) {
        personIds.push(medium.PersonId);
      }
      // tagged persons
      if (medium.Tags) {
        medium.Tags.forEach(tag => {
          const personId = tag?.tgid?.v;
          if (personId) {
            if (personIds.indexOf(personId) < 0) {
              personIds.push(personId);
            }
          }
        });
      }

      arr.push({
        id: medium.MediumId,
        fext: medium.Fext,
        personIds,
        name: medium.Data?.Name ?? null,
        description: medium.Data?.Description ?? null,
        createdAt: medium.Data?.CreatedAt ?? null,
        place: medium.Data?.Place ?? null,
        url: medium.Url ?? ancestryMediaUrl(medium.MediumId, medium.Fext),
        thumbUrl: medium.ThumbnailUrl ?? ancestryThumbUrl(medium.MediumId, medium.Fext),
      });
    }

    return arr;
  }, []);

/**
 * Transform fetched person. There are two versions of person object.
 * @param {object} item
 * @returns {object}
 */
export const transformPerson = item => {
  if (item.gid) {
    const { avatar_url: avatarUrl, gid, ...person } = item;
    return {
      ...item,
      id: gid,
      avatarUrl,
      name: person.names[0],
    };
  }

  const { avatar_url: avatarUrl, id, b, d, g, ...person } = item;
  return {
    ...person,
    id: id.replace('p#', ''),
    avatarUrl,
    birth: b,
    death: d,
    gender: g,
  };
};

const ancestryApi = (accessToken, ancestryAuth = undefined) => ({
  /**
   *
   * @param {string} title
   * @param {object} data
   * @param {object} error
   */
  async postLog(title, data, error = {}) {
    if (SETTINGS.HOST.indexOf('localhost') >= 0) {
      return;
    }

    try {
      let formattedError;
      try {
        if (typeof error === 'string') {
          formattedError = JSON.parse(error);
        } else {
          formattedError = error;
        }
        formattedError = JSON.stringify(formattedError, null, 2);
      } catch (e) {
        formattedError = error;
      }

      await apiClientPostRequest(
        'api/v1/ancestry/log',
        {
          payload: {
            blocks: [
              {
                type: 'section',
                text: {
                  type: 'mrkdwn',
                  text: `*${title}*`,
                },
              },
              {
                type: 'section',
                text: {
                  type: 'mrkdwn',
                  text: `[${SETTINGS.HOST}] Important data`,
                },
              },
              {
                type: 'section',
                text: {
                  type: 'mrkdwn',
                  text: `\`\`\`${JSON.stringify({ accessToken, data }, null, 2)}\`\`\``,
                },
              },
              {
                type: 'section',
                text: {
                  type: 'mrkdwn',
                  text: `Response from server`,
                },
              },
              {
                type: 'section',
                text: {
                  type: 'mrkdwn',
                  text: `\`\`\`${formattedError}\`\`\``,
                },
              },
            ],
          },
        },
        {
          Authorization: `Token ${accessToken}`,
        },
      );
    } catch (e) {
      console.log(e);
    }
  },

  /**
   * Get list of trees.
   * @returns {{ data: Array, error: object }}
   */
  async getTrees() {
    try {
      const response = await apiClientGetRequest('api/v1/ancestry/trees', {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      });

      const json = await response.json();

      return {
        data: (json.data || []).map(tree => ({
          id: tree.gid,
          name: tree.name,
        })),
      };
    } catch (error) {
      return { error };
    }
  },

  /**
   *
   * @param {string} treeName
   * @param {array} persons
   * @param {number} numberOfGenerations
   * @param {string} direction
   */
  async syncStatus(treeName, persons = [], numberOfGenerations, direction) {
    try {
      const response = await httpRequest(withApiGatewayHost('ancestry/sync'), {
        method: 'POST',
        body: {
          persons: persons.filter(person => !!person),
          tree_name: treeName,
          numberOfGenerations,
          direction,
        },
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      });

      const json = await response.json();

      return { data: json.data };
    } catch (error) {
      return { error };
    }
  },

  /**
   *
   * @param {string} productId sku
   * @param {string} version
   * @param {array} persons
   * @param {string} type ancestors | descendants
   */
  async syncStart(productId, version, persons = [], type = undefined) {
    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(withApiGatewayHost('ancestry/sync/new'), {
        method: 'POST',
        body: {
          product_id: productId,
          version,
          data: [
            {
              ancestry: {
                persons: persons.filter(person => !!person),
                accessToken: ancestryToken,
                type,
              },
            },
          ],
        },
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      });

      const json = await response.json();

      return { data: json.data };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Get all of persons.
   * @param {string} treeId
   * @param {string} searchText
   * @param {object} [opts]
   * @param {AbortSignal} [opts.signal]
   * @param {boolean} [opts.includeCitations=false]
   * @returns {{ data: Array, metaData: object, error: object }}
   */
  async getTreePersons(treeId, searchText, { signal, includeCitations = false } = {}) {
    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(
        withApiGatewayHost(`ancestry/filterPeople`, {
          treeId,
          filter: searchText.length > 0 ? searchText : undefined,
        }),
        {
          method: 'GET',
          headers: {
            Authorization: ancestryToken,
          },
          signal,
        },
      );

      const json = await response.json();

      const persons = (json.data ?? []).map(transformPerson);

      if (!includeCitations || persons.length === 0) {
        return { data: persons };
      }

      // Get citations for all persons
      const personIds = persons.map(person => person.id);
      const citationsResult = await this.checkPersonCitations(personIds);

      if (citationsResult.data) {
        // Create set of person IDs that have citations for fast lookup
        const citationIds = new Set(citationsResult.data);

        // Enrich persons with citation flags
        const enrichedPersons = persons.map(person => ({
          ...person,
          hasCitations: citationIds.has(person.id)
        }));

        return { data: enrichedPersons };
      }

      return { data: persons };
    } catch (error) {
      return { error };
    }
  },


  /**
   * Get persons from trees. New approach
   * @param {Array} personIds
   * @returns {{ data: Array, error: object }}
   */
  async getPersonsInDraft(personIds) {
    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(withApiGatewayHost('ancestry/findPeople'), {
        method: 'POST',
        headers: {
          Authorization: ancestryToken,
        },
        body: {
          personIds,
        },
      });

      const json = await response.json();

      return {
        data: (json.data ?? []).map(transformPerson),
      };
    } catch (error) {
      try {
        const body = JSON.parse(error.body);
        if (Array.isArray(body.invalidPersonIds)) {
          const personsNotFound = body.invalidPersonIds.reduce((acc, id) => {
            if (personIds.indexOf(id) >= 0) {
              acc.push(id);
            }
            return acc;
          }, []);

          if (personsNotFound.length > 0) {
            return { data: personsNotFound.map(id => ({ id, invalid: true })) };
          }
        }
      } catch (e) {
        console.error('FindPeople Error - Parser failed.', e);
      }

      return { error };
    }
  },

  /**
   * Get persons from synced trees. New approach
   * @param {string} treeId
   * @param {Array<string>} personIds
   * @param {Date} initialMonth
   * @returns {{ data: Array, error: object }}
   */
  async getEvents(treeId, personIds = [], initialMonth = new Date()) {
    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(
        withApiGatewayHost('ancestry/events', {
          treeId,
        }),
        {
          method: 'POST',
          headers: {
            Authorization: ancestryToken,
          },
          body: {
            person_ids: personIds,
          },
        },
      );

      const json = await response.json();

      const EVENT_TYPE = {
        BIRTH: 'Birth',
        DEATH: 'Death',
        MARRIAGE: 'Marriage',
      };
      const EVENT_TYPES = Object.entries(EVENT_TYPE).map(([, key]) => key);

      const personsHash = json.data.reduce((acc, person) => {
        const { events, ...personData } = person;
        acc[person.gid] = {
          ...personData,
        };

        if (events) {
          events.forEach(event => {
            const { Date: date, Type: type, ...eventData } = event;
            if (EVENT_TYPES.indexOf(type) >= 0) {
              try {
                if (date) {
                  const eventDate = parseDate(date, 'YYYY-MM-DD');
                  if (eventDate) {
                    acc[person.gid][type] = {
                      ...eventData,
                      date: eventDate,
                    };
                  }
                }
              } catch (e) {
                // Unsupported eventDate format
              }
            }
          });
        }

        return acc;
      }, {});

      const getYearOfEventOnCalendar = eventDate => {
        let year = initialMonth.getFullYear();
        if (eventDate.getMonth() < initialMonth.getMonth()) {
          year += 1;
        }
        return year;
      };

      const events = [];
      Object.entries(personsHash).forEach(([personId, person]) => {
        let birthDate;
        let deathDate;
        let marriageDate;

        //
        // BIRTH
        //
        if (EVENT_TYPE.BIRTH in person) {
          const event = person[EVENT_TYPE.BIRTH];
          birthDate = event.date;

          const year = getYearOfEventOnCalendar(birthDate);
          const note = ordinal(year - birthDate.getFullYear());

          events.push({
            key: uuid(),
            date: `${year}-${formatDate(birthDate, 'MM-DD')}`,
            description: `${person.name}’s ${note} 🎂`,
            type: EVENT_TYPE.BIRTH,
          });
        }

        //
        // DEATH
        //
        if (EVENT_TYPE.DEATH in person) {
          const event = person[EVENT_TYPE.DEATH];
          deathDate = event.date;

          const year = getYearOfEventOnCalendar(deathDate);
          const note = birthDate
            ? `${formatDate(birthDate, 'YYYY')} - ${formatDate(deathDate, 'YYYY')}`
            : formatDate(deathDate, 'YYYY');

          events.push({
            key: uuid(),
            date: `${year}-${formatDate(deathDate, 'MM-DD')}`,
            description: `${person.name} (${note})`, // 🪦
            type: EVENT_TYPE.DEATH,
          });
        }

        //
        // MARRIAGE
        //
        if (EVENT_TYPE.MARRIAGE in person) {
          const event = person[EVENT_TYPE.MARRIAGE];
          marriageDate = event.date;

          let description;
          if (person.gender === 'm') {
            // this person is male
            const partner = personsHash[event.PersonId];
            if (partner) {
              description = `${person.name} & ${partner.name}'s`;
            } else {
              description = `${person.name}'s`;
            }
          } else {
            // other gender
            // eslint-disable-next-line no-lonely-if
            if (personsHash[event.PersonId]?.[EVENT_TYPE.MARRIAGE]?.PersonId !== personId) {
              // only when I don't find her partner, render only her name
              description = `${person.name}'s`;
            } else {
              // OK I find partner, so do description in his branch
              description = null;
            }
          }

          // do this only if description is not undefined
          if (description) {
            const year = getYearOfEventOnCalendar(marriageDate);
            const note = ordinal(year - marriageDate.getFullYear());

            events.push({
              key: uuid(),
              date: `${year}-${formatDate(marriageDate, 'MM-DD')}`,
              description: `${description} ${note} 💒`,
              type: EVENT_TYPE.MARRIAGE,
            });
          }
        }
      });

      return {
        data: events
          .filter(event => {
            try {
              const eventDate = parseDate(event.date, 'YYYY-MM-DD');
              if (eventDate) {
                return true;
              }
            } catch (e) {
              // Unsupported eventDate format
            }
            return false;
          })
          .sort((a, b) => (a.date < b.date ? -1 : 1)),
      };
    } catch (error) {
      console.error('getEvents', { error });
      return { error };
    }
  },

  /**
   * Backend Approach.
   * Get one person.
   * @param {string} personId
   * @param {object} [opts]
   * @param {AbortSignal} [opts.signal]
   * @returns {{ data: object, error: object }}
   */
  async getPerson(personId, { signal } = {}) {
    try {
      const response = await apiClientGetRequest(`api/v1/ancestry/persons/${personId}`, {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
        signal,
      });

      const json = await response.json();

      return { data: transformPerson(json.data) };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Get many persons. TODO - update to new approach findPeople
   * @param {Array<string>} personIds
   * @param {object} [opts]
   * @param {AbortSignal} [opts.signal]
   * @returns {{ data: Array, error: object }}
   */
  async getPersons(personIds, { signal } = {}) {
    if (personIds.length === 0) {
      return { data: [] };
    }

    const responses = await Promise.all(personIds.map(id => this.getPerson(id, { signal })));

    let error;
    const data = responses.map(item => {
      if (item.error) {
        error = item.error;
        return null;
      }

      return item.data;
    });

    return { data, error };
  },

  /**
   * Get many persons.
   * @param {Array<string>} personIds
   * @param {object} [opts]
   * @param {AbortSignal} [opts.signal]
   * @returns {{ data: Array, error: object }}
   */
  async getDetailedPersons(personIds, { signal } = {}) {
    if (personIds.length === 0) {
      return { data: [] };
    }

    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(withApiGatewayHost('ancestry/people'), {
        method: 'POST',
        headers: {
          Authorization: ancestryToken,
        },
        signal,
        body: {
          personIds,
          fields: ['name', 'photo'],
        },
      });

      const json = await response.json();

      return {
        data: json.data.map(person => {
          const id = person.gid.v;
          const treeId = id.substring(id.indexOf(':'), id.length);
          const mediumId = person.ppmp?.coid ?? person.ppid;

          let avatarUrl;
          if (mediumId != null) {
            const avatar = `https://mediasvc.ancestry.com/v2/image/namespaces/1093/media/${mediumId}.jpg?client=MyCanvas`;
            avatarUrl = `${avatar}&MaxSide=5000`;
          }

          return { id, treeId, mediumId, avatarUrl };
        }),
      };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Get partners.
   * @param {string} personId
   * @param {object} [opts]
   * @param {AbortSignal} [opts.signal]
   * @returns {{ data: Array, error: object }}
   */
  async getPartners(personId, { signal } = {}) {
    const { data: personData, error: personError } = await this.getPerson(personId, { signal });

    if (personError) {
      return { error: personError };
    }

    const partnerIds = (personData.partners ?? []).map(partner => partner.gid);

    const { data: partnersData, error: partnersError } = await this.getPersons(partnerIds, {
      signal,
    });

    return { data: partnersData, error: partnersError };
  },

  /**
   * New Approach! Custom service.
   * Get media of current tree.
   * @param {string} treeId
   * @param {object} pageId Page number to fetch
   * @param {object} [opts]
   * @param {object} [opts.pageSize] Number of media per one page,
   * if undefined then the default server value is used.
   * @returns {{ data: Array, error: object }}
   */
  async getTreeMedia(treeId, pageId, { pageSize } = {}) {
    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(
        withApiGatewayHost('ancestry/media', {
          treeId,
          pageId,
          pageSize,
        }),
        {
          method: 'GET',
          headers: {
            Authorization: ancestryToken,
          },
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Backend Approach.
   * Get media of current person.
   * @param {string} personId
   * @returns {{ data: Array, error: object }}
   */
  async getPersonMedia(personId) {
    try {
      const response = await apiClientGetRequest(`api/v1/ancestry/persons/${personId}/media`, {
        headers: {
          Authorization: `Token ${accessToken}`,
        },
      });

      const json = await response.json();

      // TODO media doesn't contain fext, height, width
      return {
        data: json.data.map(item => ({ ...item, fext: item.fext ?? 'jpg' })),
      };
    } catch (error) {
      return { error };
    }
  },

  /**
   * New Approach! Custom service.
   * Get records of current person.
   * @param {string} personId
   * @returns {{ data: Array, error: object }}
   */
  async getPersonRecords(personId) {
    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(withApiGatewayHost('ancestry/records', { personId }), {
        method: 'GET',
        headers: {
          Authorization: ancestryToken,
        },
      });

      const json = await response.json();

      return {
        data: uniqBy(
          json.data
            .filter(item => item.ImageId && item.Rights)
            .map(item => ({
              id: item.ImageId,
              url: item.Rights.ViewUrl,
              thumbUrl: ancestryThumbUrl(item.ImageId, undefined, item.CollectionId),
              collectionId: item.CollectionId,
              collectionName: item.CollectionName,
              collectionTitle: item.CollectionTitle,
              categoryTitle: item.CategoryTitle,
              onlineTitle: item.OnlineTitle,
              personName: item.PersonName,
              primaryName: item.PrimaryName,
              sourceCitation: item.SourceCitation,
              imageHeight: item.Rights?.FullImageHeight,
              imageWidth: item.Rights?.FullImageWidth,
              displayFields: item.DisplayFields,
            })),
          "id",
        ),
      };
    } catch (error) {
      // 404 NOT FOUND - Empty records
      if (error.code === 404) {
        return { data: [] };
      }
      return { error };
    }
  },

  async getPersonTree(personId, numberOfGenerations, type = 'ancestors') {
    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(
        withApiGatewayHost('ancestry/tree', {
          person_id: personId,
          gen: numberOfGenerations,
          type,
        }),
        {
          method: 'GET',
          headers: {
            Authorization: ancestryToken,
          },
        },
      );

      const json = await response.json();

      return { data: json.data };
    } catch (error) {
      return { error };
    }
  },

  /**
   * Check which persons have citations available.
   * @param {Array<string>} personIds
   * @returns {{ data: Array, error: object }}
   */
  async checkPersonCitations(personIds) {
    if (!Array.isArray(personIds)) {
      return { error: { code: 400, body: 'personIds must be an array' } };
    }

    if (!ancestryAuth) {
      return { error: { code: 500, body: 'Ancestry Auth is not defined!' } };
    }

    const { data: ancestryToken, error: ancestryTokenError } = await ancestryAuth.getToken();
    if (ancestryTokenError) {
      return { error: ancestryTokenError };
    }

    try {
      const response = await httpRequest(withApiGatewayHost('ancestry/citations'), {
        method: 'POST',
        headers: {
          Authorization: ancestryToken,
        },
        body: {
          personIds,
        },
      });

      const json = await response.json();

      return { data: json.data };
    } catch (error) {
      return { error };
    }
  },
});

export default ancestryApi;
